import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {TexImageSockets} from "#material/nodes/texture/tex-image/definition"
import {texImageDefinition} from "#material/nodes/texture/tex-image/definition"
import {
    cyclesExtension,
    cyclesInterpolation,
    cyclesProjection,
    loadTextureFromImage,
    loadTextureFromImageTiles,
    selectTextureFormatAndResolutionFromOptionsAndMediaType,
} from "#material/nodes/texture/tex-image/helpers"
import type {ImageTextureExtension, ImageTextureInterpolation, ImageTextureProjection} from "#material/nodes/texture/tex-image/types"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {Three as THREE} from "#material/three/exports"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {DataObjectWithRelated, TypedImageData} from "@cm/utils"
import {ImageColorSpace} from "@cm/utils"
import type {ThreeReturns} from "#material/sockets/three"
import {MaterialConnection} from "#material/nodes/base/connection/material-connection"
import {getDataObjectFromImageResourceForFormatAndResolution} from "#material/types/data-object-from-image-resource"
import {RenderNodes} from "@cm/render-nodes"
import type {ImageTextureNode} from "#material/cycles/types"
import type {DataResolver} from "#material/validation/data-resolver/index"
import {isTypedImageData} from "@cm/utils/typed-image-data"

@registerMaterialGraphNode
export class TexImage extends MaterialNode<TexImageSockets> {
    public override get definition() {
        return texImageDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<TexImageSockets>> => {
        const imageResource = await this.resolveImageResource(evaluator.context.resolve)

        const {imageColorspaceSettingsName, extension, interpolation, textureSetRevisionId} = this.parameters.settings

        const getInputColorSpace = (imageColorspaceSettingsName?: string) => {
            switch (imageColorspaceSettingsName) {
                case "Linear":
                    return THREE.LinearSRGBColorSpace
                case "Non-Color":
                    return THREE.NoColorSpace
                case "sRGB":
                    return THREE.SRGBColorSpace
            }
            return undefined
        }

        const getWrapping = (extension?: string) => {
            switch (extension) {
                case "REPEAT":
                    return THREE.RepeatWrapping
                case "EXTEND":
                    return THREE.ClampToEdgeWrapping
                case "MIRROR":
                    return THREE.MirroredRepeatWrapping
            }
            return undefined
        }

        const alphaIsImportant = [...this.parents].some(
            (parent) => parent instanceof MaterialConnection && parent.parameters.key === "alpha" && parent.destination !== null,
        )

        const wrapping = getWrapping(extension)
        const {textureStreamingMode, logger} = evaluator.context
        if (!wrapping) logger.warn(`Warning: Unsupported extension mode: ${extension}, using repeat instead`)

        const uvNode = (await evaluator.get(this.parameters.inputs.vector))?.toVec3()

        if (!isTypedImageData(imageResource)) {
            evaluator.context.registerImageDataObject?.(imageResource, textureSetRevisionId)
        }
        const hasTiles = textureStreamingMode !== "off" && !isTypedImageData(imageResource) && !!imageResource.imageTilesDescriptor

        if (hasTiles) {
            return await loadTextureFromImageTiles(
                evaluator,
                uvNode,
                imageResource,
                interpolation,
                wrapping,
                alphaIsImportant,
                getInputColorSpace(imageColorspaceSettingsName),
            )
        } else {
            return await loadTextureFromImage(
                evaluator,
                uvNode,
                imageResource,
                interpolation,
                wrapping,
                alphaIsImportant,
                this.parameters.settings.disableProgressiveLoading ?? false,
                this.parameters.settings.forceOriginalResolution ?? false,
                getInputColorSpace(imageColorspaceSettingsName),
            )
        }
    }

    resolveImageResource = async (resolve: DataResolver): Promise<DataObjectWithRelated | TypedImageData> => {
        const inMemoryImageData = this.parameters.settings.inMemoryImageData
        if (inMemoryImageData) return inMemoryImageData

        const dataObjectLegacyId = this.parameters.settings.dataObjectLegacyId
        if (dataObjectLegacyId) {
            return await resolve.dataObjectLegacy(dataObjectLegacyId)
        }

        const dataObjectId = this.parameters.settings.dataObjectId
        if (dataObjectId) {
            return await resolve.dataObject(dataObjectId)
        }

        throw new Error(`Failed to resolve image resource for TexImage node`)
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<TexImageSockets>> => {
        const imageResource = await this.resolveImageResource(evaluator.context.resolve)
        if (isTypedImageData(imageResource)) throw new Error("In-memory images are not supported in Cycles renderer")

        evaluator.context.registerImageDataObject?.(imageResource, this.parameters.settings.textureSetRevisionId)

        const {textureResolution, forceFiltering, textureStreamingEnabled} = evaluator.context

        // Tile descriptors are only ever generated for the original data object, never for a
        // resized variant, so streamed textures must keep referencing the original: the render
        // engine looks up tiles by this same id (see getImageTilesDescriptor in the interactive
        // preview), and substituting a smaller variant here would silently defeat tiling.
        const hasTiles = textureStreamingEnabled && !!imageResource.imageTilesDescriptor

        const selectedDataObject = (() => {
            if (textureResolution === "original" || hasTiles) return imageResource
            else {
                const alphaIsImportant = [...this.parents].some(
                    (parent) => parent instanceof MaterialConnection && parent.parameters.key === "alpha" && parent.destination !== null,
                )

                const {primary} = selectTextureFormatAndResolutionFromOptionsAndMediaType(
                    evaluator.context,
                    imageResource.mediaType,
                    alphaIsImportant,
                    this.parameters.settings.forceOriginalResolution ?? false,
                )

                return getDataObjectFromImageResourceForFormatAndResolution(imageResource, primary.format, primary.resolution, evaluator.context.logger)
            }
        })()

        const {legacyId} = selectedDataObject
        if (legacyId === undefined) throw new Error(`Image legacy id could not be resolved`)

        const interpolation = cyclesInterpolation(this.parameters.settings.interpolation as ImageTextureInterpolation)

        const forceFilteringSetting = forceFiltering ? (forceFiltering === "nearest" ? "closest" : "linear") : undefined
        const overriddenInterpolation = forceFilteringSetting ? (interpolation === "cubic" ? interpolation : forceFilteringSetting) : interpolation

        const getInputColorSpace = (imageColorspaceSettingsName?: string) => {
            switch (imageColorspaceSettingsName) {
                case "Linear":
                    return ImageColorSpace.Linear
                case "Non-Color":
                    return ImageColorSpace.Linear
                case "sRGB":
                    return ImageColorSpace.Srgb
            }
            return undefined
        }

        const colorSpace = selectedDataObject.imageColorSpace ?? getInputColorSpace(this.parameters.settings.imageColorspaceSettingsName)

        const imageTextureNode: ImageTextureNode = {
            type: "image_texture",
            inputs: {vector: (await evaluator.get(this.parameters.inputs.vector)) ?? undefined},
            parameters: {
                colorspace: colorSpace === ImageColorSpace.Srgb ? "__builtin_srgb" : "__builtin_raw",
                extension: cyclesExtension(this.parameters.settings.extension as ImageTextureExtension),
                interpolation: overriddenInterpolation,
                projection: cyclesProjection(this.parameters.settings.projection as ImageTextureProjection),
                projection_blend: this.parameters.settings.projectionBlend,
                alpha_type: undefined,
                animated: undefined,
                tiles: undefined,
                filename: undefined,
            },
            resources: {
                image: RenderNodes.loadImage(legacyId),
            },
        }

        return {color: [imageTextureNode, "color"], alpha: [imageTextureNode, "alpha"]}
    }
}
